import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { facilityAdmin } from "../../api";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import BRAND from "../../brand";
import Icon from "../../components/Icons";

const P = BRAND.colorPrimary;
const A = BRAND.colorAccent;
const GOOD = "#16A34A";
const BAD = "#DC2626";

const todayStr = (offsetDays = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
};

const hourLabel = (h) => `${h % 12 === 0 ? 12 : h % 12} ${h < 12 ? "AM" : "PM"}`;

function ago(iso) {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} min ago`;
  return `${Math.floor(mins / 60)} h ago`;
}

// "▲ 12% vs. yesterday" — colour says whether the change is good news.
function Delta({ today, yesterday, lowerIsBetter = false, unit = "pct" }) {
  if (yesterday == null || today == null) return <span style={S.deltaMuted}>vs. yesterday: no data</span>;
  const diff = today - yesterday;
  if (diff === 0) return <span style={S.deltaMuted}>same as yesterday</span>;
  let text;
  if (unit === "min") {
    text = `${Math.abs(Math.round(diff * 10) / 10)} min`;
  } else if (yesterday === 0) {
    return <span style={S.deltaMuted}>yesterday: 0</span>;
  } else {
    text = `${Math.abs(Math.round((diff / yesterday) * 100))}%`;
  }
  const good = lowerIsBetter ? diff < 0 : diff > 0;
  return (
    <span style={{ color: good ? GOOD : BAD, fontSize: "12px", fontWeight: 600 }}>
      {diff > 0 ? "▲" : "▼"} {text} <span style={S.deltaMuted}>vs. yesterday</span>
    </span>
  );
}

function StatCard({ icon, tint, label, value, delta }) {
  return (
    <div style={{ ...S.card, borderLeft: `4px solid ${tint}`, padding: "16px 18px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
        <span style={{ ...S.iconBubble, background: `${tint}22`, color: tint }}><Icon name={icon} size={18} /></span>
        <span style={{ fontSize: "13px", fontWeight: 600, color: "var(--text-secondary)" }}>{label}</span>
      </div>
      <div style={{ fontSize: "30px", fontWeight: 700, color: "var(--text-primary)", lineHeight: 1.1, marginBottom: "6px" }}>{value}</div>
      <div style={{ minHeight: "16px" }}>{delta}</div>
    </div>
  );
}

function StaffOnBreak() {
  const [away, setAway] = useState([]);
  useEffect(() => {
    facilityAdmin.listStaff?.().then(r => {
      const list = r?.data?.results || r?.data || [];
      setAway(list.filter(s => s.is_away));
    }).catch(() => {});
  }, []);
  if (!away.length) return null;
  return (
    <div style={{ ...S.card, borderLeft: `4px solid ${A}`, marginBottom: "20px", padding: "12px 18px" }}>
      <div style={{ fontSize: "12px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--text-secondary)", marginBottom: "8px" }}>
        Staff on break ({away.length})
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
        {away.map(s => (
          <span key={s.id} style={S.chip}>{s.name}{s.station_name ? ` · ${s.station_name}` : ""}</span>
        ))}
      </div>
    </div>
  );
}

const ACTIONS = [
  { icon: "layers",  title: "Zones & Stations",  desc: "Configure departments and counters",        path: "/admin/zones" },
  { icon: "shuffle", title: "Queue Override",    desc: "Call, transfer or prioritise tickets",      path: "/admin/queue-override" },
  { icon: "tag",     title: "Insurance / Prefixes", desc: "Manage ticket categories and numbering", path: "/admin/insurance" },
  { icon: "users",   title: "Staff Management",  desc: "Add staff, assign stations and set access", path: "/admin/staff" },
  { icon: "monitor", title: "Display Board",     desc: "Configure patient-facing display screens",  path: "/admin/display" },
  { icon: "speaker", title: "Audio & Sounds",    desc: "Upload chimes, voice announcements and alerts", path: "/admin/audio" },
];

export default function FacilityAdminDashboardModern() {
  const { user } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const navigate = useNavigate();
  const [today, setToday] = useState(null);
  const [yesterday, setYesterday] = useState(null);
  const [live, setLive] = useState({ live: [], activity: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      facilityAdmin.dailyReport({ date: todayStr(0) }).catch(() => null),
      facilityAdmin.dailyReport({ date: todayStr(-1) }).catch(() => null),
    ]).then(([t, y]) => {
      setToday(t?.data || null);
      setYesterday(y?.data || null);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    let alive = true;
    const load = () => facilityAdmin.liveOverview().then(r => alive && setLive(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 15000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const chartData = useMemo(() => {
    const tv = today?.hourly_volume || {};
    const yv = yesterday?.hourly_volume || {};
    const hours = [...Object.keys(tv), ...Object.keys(yv)].map(Number);
    const lo = Math.min(7, ...(hours.length ? hours : [7]));
    const hi = Math.max(17, ...(hours.length ? hours : [17]));
    const rows = [];
    for (let h = lo; h <= hi; h++) rows.push({ hour: hourLabel(h), Today: tv[h] || 0, Yesterday: yv[h] || 0 });
    return rows;
  }, [today, yesterday]);

  const first = (user?.name || "").split(" ")[0] || "there";
  const dateText = new Date().toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
  const grid = isDark ? "rgba(255,255,255,0.08)" : "rgba(15,30,61,0.08)";
  const axis = isDark ? "#94A3B8" : "#64748B";

  return (
    <div style={S.root}>
      <div style={S.header}>
        <div>
          <h1 style={S.h1}>Welcome back, {first}</h1>
          <div style={S.sub}>Here's what's happening with your queues today.</div>
        </div>
        <div style={S.datePill}><Icon name="calendar" size={16} /> {dateText}</div>
      </div>

      <div style={S.statsRow}>
        <StatCard icon="ticket" tint={P} label="Tickets Issued" value={loading ? "…" : (today?.total_tickets ?? 0)}
          delta={<Delta today={today?.total_tickets} yesterday={yesterday?.total_tickets} />} />
        <StatCard icon="users" tint={GOOD} label="Patients Served" value={loading ? "…" : (today?.completed ?? 0)}
          delta={<Delta today={today?.completed} yesterday={yesterday?.completed} />} />
        <StatCard icon="hourglass" tint="#D97706" label="Currently Waiting" value={loading ? "…" : (today?.still_in_queue ?? 0)}
          delta={<span style={S.deltaMuted}>right now</span>} />
        <StatCard icon="x" tint={BAD} label="Abandoned" value={loading ? "…" : (today?.abandoned ?? 0)}
          delta={<Delta today={today?.abandoned} yesterday={yesterday?.abandoned} lowerIsBetter />} />
        <StatCard icon="clock" tint="#7C3AED" label="Avg. Waiting Time" value={loading ? "…" : `${Math.max(0, Math.round(today?.avg_wait_minutes ?? 0))} min`}
          delta={<Delta today={Math.max(0, today?.avg_wait_minutes ?? 0)} yesterday={yesterday ? Math.max(0, yesterday.avg_wait_minutes ?? 0) : null} lowerIsBetter unit="min" />} />
      </div>

      <StaffOnBreak />

      <div style={S.row2}>
        <div style={S.card}>
          <div style={S.cardTitleRow}>
            <h2 style={S.h2}>Queue Volume</h2>
            <div style={{ display: "flex", gap: "14px", fontSize: "12px", color: "var(--text-secondary)" }}>
              <span><span style={{ ...S.dot, background: P }} /> Today</span>
              <span><span style={{ ...S.dot, background: A }} /> Yesterday</span>
            </div>
          </div>
          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer>
              <AreaChart data={chartData} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="gToday" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={P} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={P} stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="gYest" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={A} stopOpacity={0.25} />
                    <stop offset="100%" stopColor={A} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={grid} vertical={false} />
                <XAxis dataKey="hour" tick={{ fill: axis, fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis allowDecimals={false} tick={{ fill: axis, fontSize: 11 }} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid var(--surface-border)", background: "var(--surface-bg)", color: "var(--text-primary)", fontSize: 12 }} />
                <Area type="monotone" dataKey="Yesterday" stroke={A} strokeWidth={2} fill="url(#gYest)" />
                <Area type="monotone" dataKey="Today" stroke={P} strokeWidth={2.5} fill="url(#gToday)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div style={{ fontSize: "11px", color: "var(--text-muted)", marginTop: "4px" }}>Tickets issued per hour.</div>
        </div>

        <div style={S.card}>
          <div style={S.cardTitleRow}>
            <h2 style={S.h2}><span style={{ color: P, marginRight: 8, display: "inline-flex", verticalAlign: "middle" }}><Icon name="signal" size={18} /></span>Live Queue</h2>
          </div>
          <div style={S.tableHead}>
            <span style={{ flex: 1 }}>Zone / Department</span>
            <span style={{ width: "96px", textAlign: "center" }}>Now Serving</span>
            <span style={{ width: "56px", textAlign: "right" }}>Waiting</span>
          </div>
          {live.live.length === 0 ? (
            <div style={S.empty}>No stations set up yet.</div>
          ) : live.live.map((r, i) => (
            <div key={i} style={S.tableRow}>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>{r.station}</span>
                <span style={{ display: "block", fontSize: "11px", color: "var(--text-muted)" }}>{r.zone}</span>
              </span>
              <span style={{ width: "96px", textAlign: "center" }}>
                {r.serving.length ? (
                  <span style={{ ...S.servingPill, background: `${P}22`, color: P }}>
                    {r.serving[0]}{r.serving.length > 1 ? ` +${r.serving.length - 1}` : ""}
                  </span>
                ) : <span style={{ color: "var(--text-muted)" }}>—</span>}
              </span>
              <span style={{ width: "56px", textAlign: "right", fontWeight: 700, color: "var(--text-primary)" }}>{r.waiting}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={S.row3}>
        <div style={S.card}>
          <h2 style={{ ...S.h2, marginBottom: "14px" }}>Quick Actions</h2>
          <div style={S.actionGrid}>
            {ACTIONS.map(a => (
              <button key={a.path} onClick={() => navigate(a.path)} style={S.action}>
                <span style={{ ...S.iconBubble, background: `${P}1A`, color: P, width: 38, height: 38 }}><Icon name={a.icon} size={20} /></span>
                <span style={{ flex: 1, textAlign: "left", minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: "14px", fontWeight: 700, color: "var(--text-primary)" }}>{a.title}</span>
                  <span style={{ display: "block", fontSize: "12px", color: "var(--text-secondary)", marginTop: 2 }}>{a.desc}</span>
                </span>
                <Icon name="arrow" size={16} color="var(--text-faint)" />
              </button>
            ))}
          </div>
        </div>

        <div style={S.card}>
          <div style={S.cardTitleRow}>
            <h2 style={S.h2}><span style={{ color: P, marginRight: 8, display: "inline-flex", verticalAlign: "middle" }}><Icon name="clock" size={18} /></span>Recent Activity</h2>
            <button onClick={() => navigate("/admin/reports")} style={S.link}>View Reports →</button>
          </div>
          {live.activity.length === 0 ? (
            <div style={S.empty}>No calls yet today.</div>
          ) : live.activity.map((e, i) => (
            <div key={i} style={S.activityRow}>
              <span style={{ ...S.iconBubble, width: 28, height: 28, background: e.type === "served" ? `${GOOD}22` : `${P}22`, color: e.type === "served" ? GOOD : P }}>
                <Icon name={e.type === "served" ? "check" : "bell"} size={14} />
              </span>
              <span style={{ flex: 1, fontSize: "13px", color: "var(--text-primary)", minWidth: 0 }}>
                Ticket <strong>{e.ticket_number}</strong> {e.type === "served" ? "served" : "called"}{e.counter ? ` at ${e.counter}` : e.station ? ` at ${e.station}` : ""}
              </span>
              <span style={{ fontSize: "11px", color: "var(--text-muted)", whiteSpace: "nowrap" }}>{ago(e.at)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const S = {
  root: { padding: "28px 32px 40px", fontFamily: "'DM Sans','Segoe UI',sans-serif", maxWidth: "1400px" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px", flexWrap: "wrap", marginBottom: "22px" },
  h1: { margin: 0, fontSize: "26px", fontWeight: 700, color: "var(--text-primary)" },
  sub: { fontSize: "14px", color: "var(--text-secondary)", marginTop: "4px" },
  datePill: { display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", fontWeight: 600, color: "var(--text-primary)", background: "var(--surface-bg)", border: "1px solid var(--surface-border)", borderRadius: "10px", padding: "9px 14px" },
  statsRow: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: "14px", marginBottom: "20px" },
  card: { background: "var(--surface-bg)", border: "1px solid var(--surface-border)", borderRadius: "14px", padding: "18px 20px", minWidth: 0 },
  iconBubble: { width: 32, height: 32, borderRadius: "10px", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  deltaMuted: { fontSize: "12px", color: "var(--text-muted)" },
  chip: { fontSize: "12px", color: "var(--text-secondary)", background: "var(--input-bg)", border: "1px solid var(--surface-border)", borderRadius: "6px", padding: "4px 10px" },
  row2: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: "16px", marginBottom: "16px" },
  row3: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: "16px" },
  cardTitleRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px", gap: "10px" },
  h2: { margin: 0, fontSize: "17px", fontWeight: 700, color: "var(--text-primary)" },
  dot: { display: "inline-block", width: 9, height: 9, borderRadius: "50%", marginRight: 6 },
  tableHead: { display: "flex", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-muted)", padding: "8px 4px", borderBottom: "1px solid var(--surface-border)" },
  tableRow: { display: "flex", alignItems: "center", padding: "10px 4px", borderBottom: "1px solid var(--surface-border)", fontSize: "14px" },
  servingPill: { display: "inline-block", fontSize: "12px", fontWeight: 700, borderRadius: "999px", padding: "3px 12px" },
  empty: { color: "var(--text-muted)", fontSize: "13px", padding: "18px 4px" },
  actionGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: "12px" },
  action: { display: "flex", alignItems: "center", gap: "12px", background: "var(--input-bg)", border: "1px solid var(--surface-border)", borderRadius: "12px", padding: "14px", cursor: "pointer", fontFamily: "inherit", width: "100%" },
  activityRow: { display: "flex", alignItems: "center", gap: "10px", padding: "9px 0", borderBottom: "1px solid var(--surface-border)" },
  link: { background: "none", border: "none", color: P, fontWeight: 600, fontSize: "12px", cursor: "pointer", fontFamily: "inherit" },
};
